<template>
  <div class="max-w-4xl mx-auto px-4 dark:bg-gray-900 dark:text-gray-100">
    <!-- Header -->
    <div class="mb-6">
      <nav class="text-sm text-gray-600 dark:text-gray-400 mb-2">
        <router-link to="/" class="hover:text-blue-600">Home</router-link>
        <span class="mx-2">›</span>
        <span>{{ year }} {{ subject }}</span>
      </nav>

      <div class="flex justify-between items-center">
        <div>
          <h2 class="text-3xl font-bold text-gray-800 dark:text-gray-100">
            Choose Your {{ subject }} Exam
          </h2>
          <p class="text-gray-600 dark:text-gray-300 mt-2">{{ year }} Level</p>
        </div>
        <!-- Dark Mode Toggle -->
        <div class="flex items-center space-x-2">
          <input type="checkbox" id="darkModeSwitch" :checked="darkMode" @change="toggleDarkMode" class="hidden" />
          <label for="darkModeSwitch" class="cursor-pointer">
            <div class="relative">
              <div class="w-10 h-6 bg-gray-400 rounded-full shadow-inner"></div>
              <div
                class="absolute w-4 h-4 bg-white rounded-full shadow top-1 left-1 transition-transform"
                :class="{ 'translate-x-4': darkMode }"
              ></div>
            </div>
          </label>
          <span class="text-sm">Dark Mode</span>
        </div>
		    </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-12">
      <div class="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
      <p class="mt-2 text-gray-600 dark:text-gray-400">Loading exam sets...</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
      <p class="font-bold">Error</p>
      <p>{{ error }}</p>
      <router-link to="/" class="block mt-4 text-blue-600 hover:underline">Return to Home</router-link>
    </div>

    <!-- Exam Sets -->
    <div v-else class="space-y-6">
      <!-- Info Banner -->
      <div v-if="!currentUser" class="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <h3 class="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">Sample vs Full Exams</h3>
        <p class="text-sm text-blue-700 dark:text-blue-300">• <strong>Sample Exam:</strong> 5 questions for practice</p>
        <p class="text-sm text-blue-700 dark:text-blue-300">• <strong>Full Exam:</strong> 20+ questions with results (requires sign-up)</p>
      </div>
      <div v-else class="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-700 rounded-lg p-4">
        <h3 class="text-sm font-medium text-green-800 dark:text-green-200 mb-2">You're Logged In!</h3>
        <p class="text-sm text-green-700 dark:text-green-300">• You have access to <strong>Full Exams</strong>.</p>
        <p class="text-sm text-green-700 dark:text-green-300">• Your results will be tracked under "Track My Performance".</p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div
          v-for="examSet in examSets"
          :key="examSet.id"
          class="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 shadow-lg rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700"
        >
          <div class="p-6">
            <h3 class="text-xl font-bold mb-2">{{ examSet.label }}</h3>
            <p class="text-sm mb-3">{{ examSet.description }}</p>

            <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {{ examSet.questions.length }} questions
            </p>

            <!-- Action Buttons -->
            <div class="space-y-3">
              <router-link
                v-if="!currentUser"
                :to="`/exam/${year}/${subject}/${examSet.id}?mode=sample`"
                class="block w-full bg-blue-600 hover:bg-blue-700 text-white text-center font-medium py-2 px-4 rounded"
              >
                Try Sample (5 Questions)
              </router-link>
              <!-- For logged-in users, the "Attempt Full Exam" effectively replaces the sample option,
                   or you might want a different UX like disabling sample or explicitly showing both.
                   For now, hiding sample for logged-in users simplifies the choice to "Attempt Full Exam".
                   If you want logged-in users to also see a sample option, we can adjust.
               -->
              <router-link
                :to="currentUser ? `/exam/${year}/${subject}/${examSet.id}?mode=full` : `/signup?redirect=/exam/${year}/${subject}/${examSet.id}?mode=full`"
                class="block w-full bg-green-600 hover:bg-green-700 text-white text-center font-medium py-2 px-4 rounded"
              >
                {{ currentUser ? 'Attempt Full Exam' : `Full Exam (${examSet.questions.length} Questions) - Sign up` }}
              </router-link>
            </div>

            <div class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Sample Question:</p>
              <p class="text-sm italic">{{ examSet.questions[0]?.question || 'Loading...' }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-center mt-8">
        <router-link
          to="/"
          class="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-100 font-medium py-2 px-6 rounded"
        >
          ← Back to Subjects
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import subjectsData from '../assets/subjects.json'
import { useDarkMode } from '../composables/useDarkMode'
import { useAuth } from '../composables/useAuth'

export default {
  name: 'ExamSetSelectView',
  props: {
    year: { type: String, required: true },
    subject: { type: String, required: true }
  },
  setup(props) {
    const route = useRoute()
    const loading = ref(true)
    const error = ref(null)

    // ✅ Moved into setup()
    const { darkMode, toggleDarkMode } = useDarkMode()
    const { currentUser } = useAuth()

    const examSets = computed(() => {
      try {
        const yearData = subjectsData[props.year]
        const subjectData = yearData?.[props.subject]
        return subjectData?.examSets || []
      } catch (err) {
        console.error('Error computing exam sets:', err)
        return []
      }
    })

    const loadExamSets = () => {
      try {
        const yearData = subjectsData[props.year]
        const subjectData = yearData?.[props.subject]

        if (!yearData) {
          error.value = `Year "${props.year}" not found`
        } else if (!subjectData) {
          error.value = `Subject "${props.subject}" not found for ${props.year}`
        } else if (!subjectData.examSets?.length) {
          error.value = `No exam sets available for ${props.subject} in ${props.year}`
        }

        loading.value = false
      } catch (err) {
        console.error('Error loading exam sets:', err)
        error.value = 'Failed to load exam sets. Please try again.'
        loading.value = false
      }
    }

    onMounted(() => {
      loadExamSets()
    })

    return {
      loading,
      error,
      examSets,
      darkMode,
      toggleDarkMode,
      currentUser
    }
  }
}
</script>

